import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScryfallCard } from '@/types/scryfall';
import { ExternalLink, Share2, Plus, ShoppingBag, MoreVertical, Palette } from 'lucide-react';
import { Separator } from './ui/separator';
import { useAppContext } from '@/hooks/useAppContext';
import { ScrollArea } from './ui/scroll-area';
import { useState, useEffect } from 'react';
import { ShareCardDialog } from './ShareCardDialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { MTGCard } from './MTGCard';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { PrintingSelector } from './PrintingSelector';

interface CardDetailsDialogProps {
  card: ScryfallCard;

  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddToList?: (card: ScryfallCard) => void;
}

const formatOracleText = (text: string) => {
  const parts = text.split(/(\(.*?\))/g);
  return parts.map((part, index) =>
    part.startsWith('(') && part.endsWith(')') ? (
      <i key={index} className="text-muted-foreground">{part}</i>
    ) : (
      part
    )
  );
};

export function CardDetailsDialog({ card, open, onOpenChange, onAddToList }: CardDetailsDialogProps) {
  const [isShareSheetOpen, setShareSheetOpen] = useState(false);
  const [isPrintingSelectorOpen, setPrintingSelectorOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(card);
  const { config } = useAppContext();
  
  // Use the existing card data instead of fetching again
  const fullCard = selectedCard;

  useEffect(() => {
    setSelectedCard(card);
  }, [card]);

  const handleAdd = () => {
    if (onAddToList) {
      onAddToList(selectedCard);
      onOpenChange(false);
    }
  };

  const handlePrintingSelect = (oldCardId: string, newCard: ScryfallCard) => {
    setSelectedCard(newCard);
  };

  const tcgPlayerUrl = fullCard?.tcgplayer_id
    ? `${config.tcgPlayerAffiliateUrl}?subId1=card-detail-buy-nonfoil&u=https%3A%2F%2Fwww.tcgplayer.com%2Fproduct%2F${fullCard.tcgplayer_id}%3Fpage%3D1`
    : '';

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedCard.name}</DialogTitle>
            {fullCard?.lang && fullCard.lang !== 'en' && (
              <div className="text-sm text-muted-foreground">{fullCard.lang.toUpperCase()}</div>
            )}
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {fullCard?.layout === 'split' ? (
                <div className="sm:col-span-2 flex items-center justify-center h-96">
                  <img
                    src={fullCard?.image_uris?.normal}
                    alt={card.name}
                    className="rounded-xl max-h-full transform rotate-90"
                  />
                </div>
              ) : !fullCard?.card_faces || !fullCard.card_faces[0].image_uris ? (
                <MTGCard
                  card={fullCard}
                  className="rounded-[1.25rem] sm:col-span-2 max-w-[16rem] sm:max-w-none mx-auto w-full"
                  imageClassName="rounded-xl"
                  showCount={false}
                  enable3D={true}
                />
              ) : (
                <>
                  <MTGCard
                    card={{ ...fullCard, image_uris: fullCard.card_faces[0].image_uris }}
                    className="rounded-xl max-w-[16rem] sm:max-w-none mx-auto w-full"
                    imageClassName="rounded-xl"
                    showCount={false}
                    enable3D={true}
                  />
                  <MTGCard
                    card={{ ...fullCard, image_uris: fullCard.card_faces[1].image_uris }}
                    className="rounded-xl"
                    imageClassName="rounded-xl"
                    showCount={false}
                    enable3D={true}
                  />
                </>
              )}
            </div>
            <ScrollArea className="h-40">
              <div className="text-sm whitespace-pre-wrap pr-6">
                <p className="text-muted-foreground">{fullCard?.type_line}</p>
                {fullCard?.card_faces ? (
                  <>
                    <p>{formatOracleText(fullCard.card_faces[0].oracle_text || '')}</p>
                    {fullCard.card_faces[0].power && fullCard.card_faces[0].toughness && (
                      <p className="font-bold">{fullCard.card_faces[0].power}/{fullCard.card_faces[0].toughness}</p>
                    )}
                    {fullCard.card_faces[0].loyalty && (
                      <p className="font-bold">Loyalty: {fullCard.card_faces[0].loyalty}</p>
                    )}
                    <Separator className="my-2" />
                    <p>{formatOracleText(fullCard.card_faces[1].oracle_text || '')}</p>
                    {fullCard.card_faces[1].power && fullCard.card_faces[1].toughness && (
                      <p className="font-bold">{fullCard.card_faces[1].power}/{fullCard.card_faces[1].toughness}</p>
                    )}
                    {fullCard.card_faces[1].loyalty && (
                      <p className="font-bold">Loyalty: {fullCard.card_faces[1].loyalty}</p>
                    )}
                  </>
                ) : (
                  <>
                    <p>{formatOracleText(fullCard?.oracle_text || '')}</p>
                    {fullCard?.power && fullCard?.toughness && (
                      <p className="font-bold">{fullCard.power}/{fullCard.toughness}</p>
                    )}
                    {fullCard?.loyalty && (
                      <p className="font-bold">Loyalty: {fullCard.loyalty}</p>
                    )}
                  </>
                )}
              </div>
            </ScrollArea>
          </div>
          <DialogFooter className="flex-row justify-between items-center w-full">
            <div className="flex gap-2">
              <Button asChild variant="outline" className="text-xs sm:text-sm">
                <a href={fullCard?.scryfall_uri} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Scryfall
                </a>
              </Button>
              {tcgPlayerUrl && fullCard?.prices?.usd && (
                <Button asChild variant="outline" className="text-xs sm:text-sm">
                  <a href={tcgPlayerUrl} target="_blank" rel="noopener noreferrer">
                    <ShoppingBag className="h-4 w-4" />
                    {`$ ${fullCard.prices.usd}`}
                  </a>
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {onAddToList && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button onClick={handleAdd} size="icon">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Add to List</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setShareSheetOpen(true)}>
                    <Share2 className="h-4 w-4 mr-2" />
                    Share to Nostr
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setPrintingSelectorOpen(true)}>
                    <Palette className="h-4 w-4 mr-2" />
                    Select Printing
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ShareCardDialog card={selectedCard} open={isShareSheetOpen} onOpenChange={setShareSheetOpen} />
      <PrintingSelector
        card={selectedCard}
        open={isPrintingSelectorOpen}
        onOpenChange={setPrintingSelectorOpen}
        onSelect={handlePrintingSelect}
      />
    </>
  );
}